import { Container, Graphics, Rectangle, Sprite, Texture } from 'pixi.js';
import { GreyVeilFilter } from '../../effects/GreyVeilFilter';
import {
  boundsOf,
  lanternFlicker,
  roadLanterns,
  type Box,
  type Lantern,
  type WorldMapSpec,
} from '../../logic/realm/worlds';

/** The veil's mask, in mask pixels per master pixel. */
const MASK_SCALE = 1 / 8;
/** How soft the veil's edge is, in mask pixels. */
const MASK_BLUR = 3;

function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * A world map's locked buildings, dimmed and greyed in code (the paintings
 * have no closed versions): a copy of the map tiles over each locked
 * building's outline, greyed by {@link GreyVeilFilter} through a soft mask,
 * so the painting round it shows through unchanged. Each building has a
 * level, 1 locked to 0 open; the opening moment runs it down to 0, colour
 * flowing back. The filter runs only over the locked buildings on screen.
 */
export class LockedVeil {
  readonly root = new Container();
  private readonly canvas: HTMLCanvasElement;
  private readonly maskTexture: Texture;
  private readonly maskSprite: Sprite;
  private readonly filter: GreyVeilFilter;
  private readonly copies = new Map<string, Sprite>();
  private readonly area = new Rectangle();
  private levels: number[];
  /** The greyed buildings' bounds, or null with none. */
  private bounds: Box | null = null;
  private tiles: ReadonlyMap<string, Sprite> = new Map();

  constructor(
    layer: Container,
    private readonly spec: WorldMapSpec,
  ) {
    const { width, height } = spec.tiles;
    this.levels = spec.buildings.map(() => 0);
    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.ceil(width * MASK_SCALE);
    this.canvas.height = Math.ceil(height * MASK_SCALE);
    this.maskTexture = Texture.from(this.canvas);
    this.maskSprite = new Sprite(this.maskTexture);
    this.maskSprite.width = width;
    this.maskSprite.height = height;
    this.filter = new GreyVeilFilter(this.maskSprite);
    // As the sea's ripple: never let Pixi skip the filter and draw the
    // copies bare; its area is always the locked buildings in view.
    this.filter.clipToViewport = false;
    this.root.label = 'locked-veil';
    this.root.eventMode = 'none';
    this.root.sortableChildren = true;
    this.root.filters = [this.filter];
    this.root.filterArea = this.area;
    this.root.visible = false;
    layer.addChild(this.root, this.maskSprite);
    this.draw();
  }

  /** Each building's level, 1 greyed to 0 in colour, in slot order. */
  setLevels(levels: readonly number[]): void {
    const next = this.spec.buildings.map((_, i) => Math.max(0, Math.min(1, levels[i] ?? 0)));
    if (next.every((v, i) => Math.abs(v - this.levels[i]!) < 1e-3)) return;
    this.levels = next;
    this.draw();
    this.sync(this.tiles);
  }

  level(i: number): number {
    return this.levels[i] ?? 0;
  }

  private draw(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = this.canvas;
    ctx.save();
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    // Each outline drawn off the canvas, its blurred shadow on it: a soft
    // edge in every browser (the canvas filter isn't everywhere).
    const away = width + 64;
    ctx.shadowOffsetX = away;
    ctx.shadowBlur = MASK_BLUR;
    let bounds: Box | null = null;
    this.spec.buildings.forEach((b, i) => {
      const level = this.levels[i]!;
      if (level <= 0.001) return;
      ctx.shadowColor = `rgba(255, 255, 255, ${level})`;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      b.outline.forEach(([x, y], k) => {
        const px = x * MASK_SCALE - away;
        const py = y * MASK_SCALE;
        if (k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fill();
      const box = boundsOf(b.outline);
      bounds = bounds
        ? {
            x: Math.min(bounds.x, box.x),
            y: Math.min(bounds.y, box.y),
            w: Math.max(bounds.x + bounds.w, box.x + box.w) - Math.min(bounds.x, box.x),
            h: Math.max(bounds.y + bounds.h, box.y + box.h) - Math.min(bounds.y, box.y),
          }
        : box;
    });
    ctx.restore();
    this.maskTexture.source.update();
    const grow = (MASK_BLUR * 2) / MASK_SCALE;
    const b = bounds as Box | null;
    this.bounds = b ? { x: b.x - grow, y: b.y - grow, w: b.w + 2 * grow, h: b.h + 2 * grow } : null;
    if (!this.bounds) this.root.visible = false;
  }

  /** Keep the copy of the map tiles in step with the scene's: only tiles
   *  under a greyed building are copied. */
  sync(tiles: ReadonlyMap<string, Sprite>): void {
    this.tiles = tiles;
    const bounds = this.bounds;
    for (const [key, s] of tiles) {
      if (this.copies.has(key) || !bounds) continue;
      if (!overlaps({ x: s.x, y: s.y, w: s.width, h: s.height }, bounds)) continue;
      const c = new Sprite(s.texture);
      c.position.copyFrom(s.position);
      c.width = s.width;
      c.height = s.height;
      c.zIndex = s.zIndex;
      this.root.addChild(c);
      this.copies.set(key, c);
    }
    for (const [key, c] of this.copies) {
      const keep =
        tiles.has(key) &&
        !!bounds &&
        overlaps({ x: c.x, y: c.y, w: c.width, h: c.height }, bounds);
      if (keep) continue;
      c.destroy();
      this.copies.delete(key);
    }
  }

  /** Run the filter over the greyed buildings in view only. */
  update(view: Box): void {
    const b = this.bounds;
    if (!b || this.copies.size === 0) {
      this.root.visible = false;
      return;
    }
    const x0 = Math.max(view.x, b.x);
    const y0 = Math.max(view.y, b.y);
    const x1 = Math.min(view.x + view.w, b.x + b.w);
    const y1 = Math.min(view.y + view.h, b.y + b.h);
    if (x1 <= x0 || y1 <= y0) {
      this.root.visible = false;
      return;
    }
    this.area.x = x0;
    this.area.y = y0;
    this.area.width = x1 - x0;
    this.area.height = y1 - y0;
    this.root.visible = true;
  }

  destroy(): void {
    for (const c of this.copies.values()) c.destroy();
    this.copies.clear();
    this.root.destroy({ children: true });
    this.maskSprite.destroy();
    this.maskTexture.destroy(true);
  }
}

/** How fast a lantern comes on, per second. */
const LANTERN_RATE = 2.5;
/** Between lanterns lighting one after another along the road. */
export const LANTERN_STEP_MS = 110;

interface LanternView {
  at: Lantern;
  /** The lamp, drawn white and tinted from dark bronze to a warm light; none
   *  for a painted lamp (the painting has its own, only its glow is drawn). */
  head: Graphics | null;
  glow: Sprite;
  /** 0 dark to 1 lit. */
  level: number;
  target: number;
  /** Waits this long before going towards its target. */
  wait: number;
}

/**
 * The road's lanterns and the soft glow on the next slot to open. The
 * lanterns are lit as far along the road as the furthest open slot, and a
 * lit one's flame flickers softly, each on its own ({@link lanternFlicker}):
 * never a strobe, still under reduced motion and while the map is covered.
 */
export class RoadLights {
  readonly root = new Container();
  private readonly views: LanternView[];
  private readonly nextGlow: Sprite;
  private nextAt: { x: number; y: number } | null = null;
  /** The flicker's and the glow's clock, seconds: it stops while paused. */
  private clock = 0;

  constructor(
    layer: Container,
    spec: WorldMapSpec,
    glow: Texture,
    private readonly still: boolean,
  ) {
    this.root.label = 'road-lights';
    this.root.eventMode = 'none';
    this.nextGlow = new Sprite(glow);
    this.nextGlow.anchor.set(0.5);
    this.nextGlow.tint = 0xffd890;
    this.nextGlow.blendMode = 'add';
    this.nextGlow.visible = false;
    this.root.addChild(this.nextGlow);
    const all = roadLanterns(spec);
    this.views = all.map((at, k) => {
      const g = new Sprite(glow);
      g.anchor.set(0.5);
      g.width = 110;
      g.height = 110;
      g.tint = 0xffc860;
      g.blendMode = 'add';
      g.alpha = 0;
      let head: Graphics | null = null;
      if (at.painted) {
        // The painting's own lamp: its glow is the only thing drawn, at the
        // point the spec gives.
        g.position.set(at.x, at.y);
        this.root.addChild(g);
      } else {
        const post = new Graphics()
          .roundRect(at.x - 2.5, at.y - 30, 5, 30, 2)
          .fill({ color: 0x3a2814, alpha: 0.9 });
        head = new Graphics().roundRect(at.x - 6, at.y - 42, 12, 13, 3).fill({ color: 0xffffff });
        const rim = new Graphics()
          .roundRect(at.x - 6, at.y - 42, 12, 13, 3)
          .stroke({ color: 0x2a1605, width: 1.5 });
        g.position.set(at.x, at.y - 34);
        this.root.addChild(post, head, rim, g);
      }
      const v: LanternView = { at, head, glow: g, level: 0, target: 0, wait: 0 };
      this.drawHead(v, k, all.length);
      return v;
    });
    layer.addChild(this.root);
  }

  private drawHead(v: LanternView, i: number, count = this.views.length): void {
    const flame = this.still ? 1 : lanternFlicker(i, count, this.clock);
    const lit = v.level * flame;
    // From dark bronze to a warm light.
    const mix = (a: number, b: number): number => Math.round(a + (b - a) * lit);
    if (v.head) v.head.tint = (mix(0x4a, 0xff) << 16) | (mix(0x36, 0xe0) << 8) | mix(0x1e, 0x8a);
    v.glow.alpha = 0.85 * lit;
  }

  /**
   * Light the lanterns up to `litTo` along the road. With `from`, those
   * past it light one after another, from there on (the opening moment);
   * otherwise at once.
   */
  light(litTo: number, from?: number): number {
    let order = 0;
    for (const [i, v] of this.views.entries()) {
      const target = v.at.s <= litTo ? 1 : 0;
      if (from !== undefined && target === 1 && v.at.s > from && !this.still) {
        v.level = 0;
        v.wait = order++ * LANTERN_STEP_MS;
      } else if (from === undefined || this.still) {
        v.level = target;
        v.wait = 0;
      }
      v.target = target;
      this.drawHead(v, i);
    }
    // How long the lighting takes.
    return order * LANTERN_STEP_MS + 1000 / LANTERN_RATE;
  }

  /** The soft glow on the next slot to open, or none. */
  setNext(at: { x: number; y: number } | null, radius = 300): void {
    this.nextAt = at;
    this.nextGlow.visible = !!at;
    if (!at) return;
    this.nextGlow.position.set(at.x, at.y);
    this.nextGlow.width = radius * 2;
    this.nextGlow.height = radius * 2;
    this.pulse();
  }

  private pulse(): void {
    const t = this.still ? 0 : this.clock;
    this.nextGlow.alpha = 0.3 + 0.12 * Math.sin((t * Math.PI * 2) / 2.6);
  }

  /** A frame of `ms`; `running` is false while the map is covered or
   *  hidden, when the flames and the glow hold still. */
  update(ms: number, running = true): void {
    if (running && !this.still) this.clock += ms / 1000;
    if (this.nextAt) this.pulse();
    this.views.forEach((v, i) => {
      if (v.level !== v.target) {
        if (v.wait > 0) {
          v.wait -= ms;
          return;
        }
        const step = (ms / 1000) * LANTERN_RATE;
        v.level =
          v.level < v.target ? Math.min(v.target, v.level + step) : Math.max(v.target, v.level - step);
      } else if (v.level === 0 || this.still || !running) {
        return;
      }
      this.drawHead(v, i);
    });
  }

  /** Each lantern's spot and how lit it is (the ambient life and tests). */
  lanterns(): { x: number; y: number; s: number; level: number }[] {
    return this.views.map((v) => ({ x: v.at.x, y: v.at.y, s: v.at.s, level: v.level }));
  }


  destroy(): void {
    this.root.destroy({ children: true });
  }
}
